import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import ShopifyBuyButton from '../components/ShopifyBuyButton';
import TrackCard from '../components/TrackCard';
import logger from '../utils/logger';
import { ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AlbumsPage — public listing of all published albums.
 * Routes:
 *   /albums       → grid of albums, each with embedded Shopify Buy Button
 *   /albums/:slug → detail page for one album + its singles
 */

const AlbumCard = ({ album }) => (
  <div className="bg-[#1A1A1A] rounded-2xl overflow-hidden border border-white/5" data-testid={`album-card-${album.slug}`}>
    {album.cover_url ? (
      <div className="aspect-square overflow-hidden bg-black">
        <img src={album.cover_url} alt={album.title} className="w-full h-full object-cover" />
      </div>
    ) : (
      <div className="aspect-square bg-gradient-to-br from-[#0A2216] via-[#1A1A1A] to-[#0D0D0D] flex items-center justify-center">
        <span className="font-['Bebas_Neue'] text-3xl text-[#D4AF37]/40 px-4 text-center">{album.title}</span>
      </div>
    )}
    <div className="p-5">
      <h3 className="font-['Bebas_Neue'] text-3xl tracking-tight">{album.title}</h3>
      {album.artist && (
        <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mt-1">{album.artist}</p>
      )}
      {album.description && (
        <p className="text-sm text-[#A3A3A3] mt-3 line-clamp-3">{album.description}</p>
      )}
      <Link
        to={`/albums/${album.slug}`}
        className="inline-block mt-4 text-sm text-[#D4AF37] uppercase tracking-wider hover:text-white transition-colors"
        data-testid={`album-open-${album.slug}`}
      >
        Open Album →
      </Link>
    </div>
  </div>
);

const AlbumsListPage = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/albums`);
        setAlbums(data || []);
      } catch (e) {
        logger.error('Failed to load albums', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#0D0D0D] px-6 md:px-12 py-12" data-testid="albums-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Full Albums</p>
          <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl">ALBUMS</h1>
          <p className="text-[#A3A3A3] mt-3 max-w-2xl">
            Own the full body of work. Buy individual songs as singles, or pick up an entire album in one move.
          </p>
        </div>
        {loading ? (
          <p className="text-[#A3A3A3]">Loading albums…</p>
        ) : albums.length === 0 ? (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-10 text-center">
            <p className="text-[#A3A3A3]">No albums published yet — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {albums.map((a) => (
              <AlbumCard key={a.album_id} album={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AlbumDetailPage = () => {
  const { slug } = useParams();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/albums/${slug}`);
        setAlbum(data);
      } catch (e) {
        setError('Album not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#0D0D0D] px-6 py-20 text-center text-[#A3A3A3]">Loading…</div>;
  if (error || !album) return <div className="min-h-screen bg-[#0D0D0D] px-6 py-20 text-center text-[#A3A3A3]">{error}</div>;

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="album-detail-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <Link
          to="/albums"
          className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors mb-8"
          data-testid="back-to-albums"
        >
          <ChevronLeft size={16} />
          All Albums
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Cover + meta */}
          <div>
            {album.cover_url ? (
              <img
                src={album.cover_url}
                alt={album.title}
                className="w-full aspect-square object-cover rounded-2xl bg-[#1A1A1A]"
              />
            ) : (
              <div className="w-full aspect-square rounded-2xl bg-gradient-to-br from-[#0A2216] via-[#1A1A1A] to-[#0D0D0D] flex items-center justify-center">
                <span className="font-['Bebas_Neue'] text-5xl text-[#D4AF37]/30 px-6 text-center">{album.title}</span>
              </div>
            )}
          </div>

          <div>
            {album.artist && (
              <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{album.artist}</p>
            )}
            <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl leading-none">{album.title}</h1>
            {album.release_date && (
              <p className="text-sm text-[#A3A3A3] mt-2">{album.release_date}</p>
            )}
            {album.description && (
              <p className="text-[#A3A3A3] mt-5 leading-relaxed">{album.description}</p>
            )}

            <div className="mt-8 p-5 bg-[#1A1A1A] border border-[#D4AF37]/20 rounded-2xl">
              {album.full_album_shopify_product_id ? (
                <>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
                    Buy the full album{album.price ? ` · $${Number(album.price).toFixed(2)}` : ''}
                  </p>
                  <ShopifyBuyButton
                    shopDomain={album.shopify_shop_domain}
                    storefrontToken={album.shopify_storefront_token}
                    componentType="product"
                    componentId={album.full_album_shopify_product_id}
                  />
                  <p className="text-xs text-[#A3A3A3] mt-4">Or pick up individual singles below.</p>
                </>
              ) : (
                <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
                  Buy singles below · Full album coming soon
                </p>
              )}
            </div>

            {album.credits && (
              <div className="mt-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-2">Credits</p>
                <p className="text-sm text-[#A3A3A3] whitespace-pre-line leading-relaxed">{album.credits}</p>
              </div>
            )}
          </div>
        </div>

        {/* User's own album track listing (uploaded tracks + linked in-app singles).
            The site is the storefront; Shopify only handles the payment. */}
        {((album.track_files && album.track_files.length > 0) || (album.tracks && album.tracks.length > 0)) && (
          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Tracklist</p>
            <h2 className="font-['Bebas_Neue'] text-3xl mb-6">Songs on this album</h2>
            {/* Uploaded audio (from album admin) */}
            {album.track_files && album.track_files.length > 0 && (
              <div className="space-y-2 mb-8">
                {album.track_files.map((t, i) => (
                  <div
                    key={(t.audio_url || '') + i}
                    className="flex items-center gap-3 bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3"
                    data-testid={`album-trackfile-${i}`}
                  >
                    <span className="text-[#D4AF37] font-['Bebas_Neue'] text-xl w-8 text-center">{i + 1}</span>
                    <span className="flex-1 text-white truncate">{t.title || `Track ${i + 1}`}</span>
                    {t.audio_url && (
                      <audio src={t.audio_url} controls preload="none" className="h-9" style={{ maxWidth: '200px' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Linked in-app singles — each gets its Shopify Buy Button via TrackCard */}
            {album.tracks && album.tracks.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {album.tracks.map((t) => (
                  <TrackCard key={t.track_id || t.id} track={t} tracks={album.tracks} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Extra artwork */}
        {album.extra_images && album.extra_images.length > 0 && (
          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Gallery</p>
            <h2 className="font-['Bebas_Neue'] text-3xl mb-6">Extra artwork</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {album.extra_images.map((url, i) => (
                <img key={url + i} src={url} alt={`extra ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AlbumsPage = () => {
  const { slug } = useParams();
  if (slug) return <AlbumDetailPage />;
  return <AlbumsListPage />;
};

export default AlbumsPage;
